import { Link } from "react-router-dom";
import { PublicHeader, Footer } from "@/components/PublicChrome";
import { Rocket, Cpu, Gamepad2, Swords, Store, Wrench, Coins, ArrowRight, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Cpu, title: "Modular Robot Assembly", body: "Fabricate a companion from swappable legs, torso, arms and heads, then pick a CPU personality and a glowing power core. Every combination is a unique unit with its own idle animation and colour signature." },
  { icon: Gamepad2, title: "The Central Core Arcade", body: "Six fast-loading skill games — Astro Dodge, Bolt Catch, Memory Core, Rhythm Hacker, Bot Racer and Circuit Snake — each rewarding Credits you can spend across the station." },
  { icon: Swords, title: "Arena & Training", body: "Fight turn-based PvE gauntlets, spar against other pilots' robots, and settle grudges against rivals who beat you. Drill your stats in the Training Bay between bouts." },
  { icon: Store, title: "A Living Economy", body: "Trade at the Market and Trading Post, snipe deals at the Auction House, bank your Credits for interest, and stock rare parts from three rotating merchant vendors." },
  { icon: Wrench, title: "The Fabrication Depot", body: "Buy new parts and hot-swap them onto your active robot at any time. The part you replace returns safely to your inventory — experiment freely." },
  { icon: Coins, title: "Quests & Progression", body: "Complete daily, weekly and story missions to raise your Station Clearance Level and unlock new rooms, vendors and locations across the orbiting platform." },
];

const PATCH_NOTES = [
  { v: "Build 1.0.7", items: ["Public landing page & open compliance pages for a fully crawlable front door.", "Full arcade sprite overhaul — every minigame now runs on hand-crafted 2D art.", "Astro Dodge radiation-cloud warnings and supernova pickup sparkle effects."] },
  { v: "Build 1.0.6", items: ["Astro Dodge rebalanced with small asteroids, healing supernovas and drifting nebula clouds.", "New Game Over flow: submit your score for Credits, or retry instantly."] },
  { v: "Build 1.0.5", items: ["Astro Dodge rebuilt with real ship, asteroid and starfield art plus a 3-point shield system."] },
];

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col grid-bg">
      <PublicHeader />

      {/* Hero */}
      <section data-testid="landing-hero" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/assets/sprites/landing_hero.png" alt="Orbit Online space station orbiting a gas giant" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center">
          <span className="inline-flex items-center gap-2 label-cap mb-5 px-3 py-1 rounded-full border border-primary/40 bg-primary/10">
            <Sparkles className="w-3.5 h-3.5" /> Free to play · No download
          </span>
          <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl neon-text leading-[1.05] mb-5">
            Build your robot.<br />Rule the station.
          </h1>
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-muted-foreground mb-8">
            Orbit Online is a futuristic robot-pet MMO set aboard a deep-space station. Assemble a modular companion, play the arcade, battle in the Arena and rise through a living player economy.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/app/login" data-testid="hero-enter-station" className="btn-primary text-base px-8 py-4">
              <Rocket className="w-5 h-5" /> Enter Station
            </Link>
            <a href="#guide" className="btn-ghost text-base px-8 py-4">Read the guide <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      <main className="flex-1">
        {/* Lore */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-head font-black text-2xl sm:text-3xl mb-5 text-primary">Welcome aboard Orbit</h2>
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              Long after the old networks went dark, a single self-repairing station kept turning above a distant amber gas giant. The pilots call it <strong className="text-foreground">Orbit</strong>. It is part shipyard, part marketplace, part arena — a place where wandering engineers dock to fabricate mechanical companions and test them against the best the sector has to offer.
            </p>
            <p>
              As a newly-arrived pilot you begin at the <strong className="text-foreground">Assembly Bay</strong>, where raw components are printed into a living robot. You choose everything: the legs it stands on, the torso that houses its reactor, the arms it fights with, and the head that gives it a face. Then you install a <strong className="text-foreground">CPU personality</strong> — cold and logical, hyperactive, weathered and rust-worn, or a dreamy holographic mind — and finally slot a glowing <strong className="text-foreground">power core</strong> that tints your unit and sets it apart from every other robot on the station.
            </p>
            <p>
              No two companions are alike. The parts you fuse together decide not just how your robot looks, but how it moves, how it idles, and how it performs in combat. Veterans are known by their silhouettes long before their names — and the station remembers.
            </p>
          </div>
        </section>

        {/* Feature grid */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h2 className="font-head font-black text-2xl sm:text-3xl mb-8 text-center">Everything you can do</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`} className="panel-glow p-6 hover:-translate-y-1 transition-transform">
                <span className="grid place-items-center w-11 h-11 rounded-[var(--radius)] bg-primary/15 text-primary mb-4"><f.icon className="w-6 h-6" /></span>
                <h3 className="font-head font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Central Core guide */}
        <section id="guide" className="max-w-3xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
          <h2 className="font-head font-black text-2xl sm:text-3xl mb-5 text-primary">A pilot's guide to the Central Core</h2>
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              The <strong className="text-foreground">Central Core</strong> is the beating heart of the station — its arcade district, where pilots earn the Credits that fuel everything else. Six machines line the concourse, and each one is a self-contained skill game with its own rhythm.
            </p>
            <p>
              <strong className="text-foreground">Astro Dodge</strong> straps you into a scout ship weaving through an asteroid field. Small rocks chip a single shield, heavy asteroids end your run instantly, and rare blue supernovas restore a lost shield — but watch the drifting nebula clouds: green slows the debris while red sends it screaming toward you. <strong className="text-foreground">Bolt Catch</strong> puts a robotic hand under a cascade of glowing energy cells; scoop the good ones and let the rusted scrap fall past. <strong className="text-foreground">Memory Core</strong> is a grid of face-down holographic chips — flip them two at a time to match faction emblems in as few moves as possible.
            </p>
            <p>
              <strong className="text-foreground">Rhythm Hacker</strong> streams laser pulses down four lanes; tap them dead-on the alignment bar to build a combo multiplier. <strong className="text-foreground">Bot Racer</strong> is a top-down sprint down a neon cyberpunk highway, dodging oncoming scrap traffic for as long as your reflexes hold. And <strong className="text-foreground">Circuit Snake</strong> is a sleek mechanical data-worm that grows longer with every microchip it absorbs — coil carefully or crash into your own tail.
            </p>
            <p>
              Every game converts your score into Credits, which you can bank for interest, spend on new parts at the Fabrication Depot, or stake against other pilots in the Arena. Play a few rounds, learn each machine's tempo, and the Central Core quickly becomes the fastest way to grow your fortune.
            </p>
          </div>
        </section>

        {/* Patch notes */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16">
          <h2 className="font-head font-black text-2xl sm:text-3xl mb-8">Latest patch notes</h2>
          <div className="space-y-6">
            {PATCH_NOTES.map((p) => (
              <div key={p.v} className="panel p-5">
                <div className="label-cap text-primary mb-3">{p.v}</div>
                <ul className="space-y-2">
                  {p.items.map((it, i) => (
                    <li key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-primary mt-1">▹</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 text-center">
          <div className="panel-glow hud-corner p-10 sm:p-14">
            <h2 className="font-head font-black text-3xl sm:text-4xl neon-text mb-4">Your first robot is waiting.</h2>
            <p className="text-muted-foreground mb-7 max-w-xl mx-auto">Create a free account, fabricate your companion, and dock with the station in under a minute.</p>
            <Link to="/app/login" data-testid="cta-enter-station" className="btn-primary text-base px-10 py-4"><Rocket className="w-5 h-5" /> Enter Station</Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
